// The page-backed source: the consumer's `load(request, {signal})` returns one page and its exact counts.
// The latest committed query, page or page size owns the visible result; a superseded response is aborted
// and, if it still arrives, discarded. A count that proves the current page invalid clamps it to the final
// valid page and requests that page once. Retry repeats the last request unchanged.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ACTIONS } from '../types'
import { pageCount, shownRange } from '../query/paging'
import { checkResponse, requestOf } from './request'

const LOADING = {
  status: 'loading',
  rows: [],
  total: null,
  eligibleTotal: null,
  aggregates: {},
  errors: [],
}

/**
 * @param {object} input
 * @param {import('../types').NormalizedConfig} input.config
 * @param {import('../types').TableState} input.state
 * @param {(action: object) => void} input.dispatch
 * @param {(request: import('./request').PageRequest, options: {signal: ?AbortSignal}) => Promise<object>} input.load
 *   resolves to {rows, total, eligibleTotal?, aggregates?}
 * @param {boolean} [input.enabled] false while the configuration is invalid
 * @param {() => import('../types').TableState} [input.current] the latest committed state, which a commit
 *   moves ahead of the render; defaults to the rendered state
 * @returns {import('./request').SourceResult & {errors: string[]}}
 */
export const usePageSource = ({
  config,
  state,
  dispatch,
  load,
  enabled = true,
  current = null,
}) => {
  const [result, setResult] = useState(LOADING)
  const sequence = useRef(0)
  const controller = useRef(null)
  const last = useRef(null)
  const corrected = useRef(null)
  const loader = useRef(load)
  loader.current = load
  const latest = useRef({ config, dispatch, state, current })
  latest.current = { config, dispatch, state, current }

  const request = useMemo(
    () => requestOf(state, config),
    // the request depends only on the committed query, page and page size
    [config.tableId, state.query, state.page, state.pageSize]
  )
  const key = JSON.stringify(request)

  // a response belongs to the committed state only while its request is still the one that state asks for;
  // a commit that lands before the render starts the next request makes the response in flight stale
  const isCurrent = useCallback((id, req) => {
    if (id !== sequence.current) return false
    const { config: cfg, state: rendered, current: read } = latest.current
    const committed = read ? read() : rendered
    return JSON.stringify(requestOf(committed, cfg)) === JSON.stringify(req)
  }, [])

  const run = useCallback((req, corrective) => {
    if (controller.current) controller.current.abort()
    const id = ++sequence.current
    const abort =
      typeof AbortController === 'undefined' ? null : new AbortController()
    controller.current = abort
    last.current = { request: req, corrective }
    setResult(prev => (prev.status === 'loading' ? prev : { ...LOADING }))

    Promise.resolve()
      .then(() => loader.current(req, { signal: abort ? abort.signal : null }))
      .then(
        response => {
          if (!isCurrent(id, req)) return
          const { config: cfg, dispatch: send } = latest.current
          const errors = checkResponse(cfg, req, response)
          if (errors.length) {
            setResult({ ...LOADING, status: 'error', errors })
            return
          }
          const final = pageCount(response.total, req.pageSize)
          if (req.page > final && !corrective) {
            corrected.current = final
            send({ type: ACTIONS.pageClamp, page: final, reason: 'response' })
            return
          }
          setResult({
            status: 'ready',
            rows: response.rows,
            total: response.total,
            eligibleTotal: cfg.selection ? response.eligibleTotal : null,
            aggregates: response.aggregates || {},
            errors: [],
          })
        },
        () => {
          if (!isCurrent(id, req)) return
          setResult({ ...LOADING, status: 'error' })
        }
      )
  }, [isCurrent])

  useEffect(() => {
    if (!enabled) return
    const corrective = corrected.current === request.page
    corrected.current = null
    run(request, corrective)
  }, [key, enabled, run])

  useEffect(
    () => () => {
      sequence.current += 1
      if (controller.current) controller.current.abort()
    },
    []
  )

  const retry = useCallback(() => {
    if (last.current) run(last.current.request, last.current.corrective)
  }, [run])

  const total = result.status === 'ready' ? result.total : null
  return {
    mode: 'page',
    status: result.status,
    errors: result.errors,
    data: result.rows,
    matching: result.rows,
    pageRows: result.rows,
    total,
    eligibleTotal: result.status === 'ready' ? result.eligibleTotal : null,
    aggregates: result.aggregates,
    currentPage: state.page,
    ...shownRange(total || 0, state.page, state.pageSize),
    retry,
  }
}
